import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import {
  createDepositCheckRequest,
  fetchDepositCheckRequests,
} from "../../services/orders";

const PAYMENT_METHOD_OPTIONS = [
  { id: "e_transfer", label: "E-transfer" },
  { id: "eft", label: "EFT" },
  { id: "cheque", label: "Cheque" },
  { id: "cash", label: "Cash" },
  { id: "wire_transfer", label: "Wire transfer" },
  { id: "other", label: "Other" },
];

const defaultDraft = {
  paymentMethod: "e_transfer",
  amount: "",
  currency: "CAD",
  requestNote: "",
  invoiceId: "",
};

const summarizeRecipients = (recipients = []) =>
  recipients
    .map((recipient) => recipient?.name || recipient?.email || `User ${recipient?.id}`)
    .filter(Boolean)
    .join(", ");

export default function DepositCheckRequestDialog({
  open,
  onClose,
  instanceUniqueId,
  orderId,
  currentUser,
  invoices = [],
  onSubmitted,
}) {
  const [draft, setDraft] = useState(defaultDraft);
  const [routing, setRouting] = useState({ configured: false, recipients: [], warnings: [] });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const hasConfiguredRecipients =
    routing?.configured === true ||
    (Array.isArray(routing?.recipients) && routing.recipients.length > 0);

  const canSubmit = useMemo(
    () =>
      Boolean(
        open &&
          !loading &&
          !submitting &&
          String(draft.requestNote || "").trim() &&
          hasConfiguredRecipients
      ),
    [draft.requestNote, hasConfiguredRecipients, loading, open, submitting]
  );

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadRouting = async () => {
      if (!instanceUniqueId || !orderId) return;
      setLoading(true);
      setFormError("");
      try {
        const response = await fetchDepositCheckRequests(orderId, instanceUniqueId);
        if (cancelled) return;
        setRouting(response?.routing || { configured: false, recipients: [], warnings: [] });
      } catch (error) {
        if (cancelled) return;
        setRouting({ configured: false, recipients: [], warnings: [] });
        setFormError(
          error?.response?.data?.message || "Unable to load Deposit Check configuration."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadRouting();

    return () => {
      cancelled = true;
    };
  }, [instanceUniqueId, open, orderId]);

  useEffect(() => {
    if (!open) {
      setDraft(defaultDraft);
      setFormError("");
      setLoading(false);
    }
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleSubmit = async () => {
    const note = String(draft.requestNote || "").trim();
    if (!note) {
      setFormError("Add a note so Finance knows what payment to verify.");
      return;
    }
    if (!hasConfiguredRecipients) {
      setFormError(
        "Deposit Check requests are unavailable until a finance or administrator recipient is configured in Financial Configuration."
      );
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      const response = await createDepositCheckRequest(orderId, {
        instanceUniqueId,
        paymentMethod: draft.paymentMethod,
        amount: draft.amount === "" ? null : draft.amount,
        currency: draft.currency || "CAD",
        requestNote: note,
        invoiceId: draft.invoiceId ? Number(draft.invoiceId) : null,
      });
      toast.success("Deposit Check request sent to Finance.");
      onSubmitted?.(response?.request);
      onClose?.();
    } catch (error) {
      setFormError(
        error?.response?.data?.message || "Unable to create Deposit Check request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request Deposit Check</DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 2, pt: 2 }}>
        <Alert severity="info">
          Ask Finance to verify an offline payment such as an e-transfer, EFT, cheque,
          cash, or wire transfer. This request does not record a payment.
        </Alert>

        {formError ? <Alert severity="error">{formError}</Alert> : null}

        {Array.isArray(routing?.warnings) && routing.warnings.length ? (
          <Alert severity="warning">{routing.warnings[0]}</Alert>
        ) : null}

        {hasConfiguredRecipients ? (
          <Box sx={{ display: "grid", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Finance recipients
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {routing.recipients.map((recipient) => (
                <Chip
                  key={recipient.id || recipient.email || recipient.name}
                  size="small"
                  label={recipient.name || recipient.email || `User ${recipient.id}`}
                  variant="outlined"
                />
              ))}
            </Stack>
          </Box>
        ) : (
          <Alert severity="warning">
            A finance or administrator recipient must be configured in Financial Configuration before
            this request can be sent.
          </Alert>
        )}

        <FormControl fullWidth size="small">
          <InputLabel id="deposit-check-payment-method-label">Payment method</InputLabel>
          <Select
            labelId="deposit-check-payment-method-label"
            value={draft.paymentMethod}
            label="Payment method"
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, paymentMethod: event.target.value }))
            }
          >
            {PAYMENT_METHOD_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Amount"
            type="number"
            size="small"
            fullWidth
            value={draft.amount}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, amount: event.target.value }))
            }
            inputProps={{ min: 0, step: "0.01" }}
          />
          <TextField
            label="Currency"
            size="small"
            fullWidth
            value={draft.currency}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, currency: event.target.value.toUpperCase() }))
            }
          />
        </Stack>

        <FormControl fullWidth size="small">
          <InputLabel id="deposit-check-invoice-label">Related invoice</InputLabel>
          <Select
            labelId="deposit-check-invoice-label"
            value={draft.invoiceId}
            label="Related invoice"
            displayEmpty
            renderValue={(value) =>
              value ? `Invoice #${value}` : "Order-level request"
            }
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, invoiceId: event.target.value }))
            }
          >
            <MenuItem value="">Order-level request</MenuItem>
            {invoices.map((invoice) => (
              <MenuItem key={invoice.id} value={String(invoice.id)}>
                Invoice #{invoice.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Request note"
          multiline
          minRows={4}
          fullWidth
          required
          value={draft.requestNote}
          onChange={(event) =>
            setDraft((prev) => ({ ...prev, requestNote: event.target.value }))
          }
          placeholder="Customer says the e-transfer was sent this morning. Please confirm whether it has been received."
        />

        {currentUser?.id ? null : (
          <Typography variant="caption" color="text.secondary">
            Requesting user context is unavailable. Finance will still see the order and note.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? "Sending..." : "Send Request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
